
<div ref="configureTab">
  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.optionRangeRequireable)">
    <panel-item>
      <label :for="selectedObjectModelProperty.idForProp('selection_type')">
        Selection Type
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['selection_type', 'type']]"
        /> </label>
      <a-select
        v-model="selectedObjectModelProperty.selection_type"
        :options="selectionTypeOptions"
        :id="selectedObjectModelProperty.idForProp('selection_type')"
        compress
      ></a-select>

    </panel-item>
  </panel-block>

  <requireable
    v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.requireable)"
    :model="selectedObjectModelProperty"
    :is-option-range-requireable="selectedObjectModelProperty.can(ObjectModelPropertyCapability.optionRangeRequireable) && selectedObjectModelProperty.selection_type === PickListSelectionType.MultiSelect"
    :min="selectedObjectModelProperty.objectModelPropertiesArray.length ? 1 : 0"
    :max="selectedObjectModelProperty.objectModelPropertiesArray.length"
  >
    <template slot="option-range-label">
      Value Selection Limit
      <property-change-detector
        :left="selectedObjectModelProperty"
        :right="selectedObjectModelProperty.publishedEquivalent"
        :properties="[['option_range_type', 'type'], ['checkbox_range_min', 'min'], ['checkbox_range_max', 'max']]"
      />
    </template>
  </requireable>

  <input-format-config v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.formattable)"
                       :key="selectedObjectModelProperty.client_id"></input-format-config>

  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.lengthable)" id="pn-char-limit">
    <panel-title>
      Character Limit
      <property-change-detector
        :left="selectedObjectModelProperty"
        :right="selectedObjectModelProperty.publishedEquivalent"
        :properties="[['character_count.min', 'min'], ['character_count.max', 'max']]"
      />
    </panel-title>
    <panel-item>
      <multi-input compress>
        <column>
          <number-input
            id="character_count_min"
            v-model="selectedObjectModelProperty.character_count.min"
            placeholder="No Min"
          ></number-input>
        </column>
        <column>
          <number-input
            v-model="selectedObjectModelProperty.character_count.max"
            placeholder="No Max"
          ></number-input>
        </column>
      </multi-input>
      <template
        v-if="NumberHelper.anyIntegers([selectedObjectModelProperty.character_count.min, selectedObjectModelProperty.character_count.max])">
        <div>
          <a-select
            v-model="selectedObjectModelProperty.character_count.show_char_limit"
            :options="charLimitOptions"
            id="show-char-limit"
            compress
          ></a-select>
        </div>
        <row>
          <column>
            <div class="sr-only">
              <label :for="selectedObjectModelProperty.idForProp('char_limit_message')">Custom Validation Error</label>
            </div>
            <expandable-textarea
              v-model="selectedObjectModelProperty.character_count.char_limit_message"
              :id="selectedObjectModelProperty.idForProp('char_limit_message')"
              placeholder="Custom Validation Error"
            ></expandable-textarea>
          </column>
        </row>
      </template>
    </panel-item>
  </panel-block>

  <date-min-maxable
    v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.dateMinMaxable)"
    :model="selectedObjectModelProperty"
  >
    <template #modeLabel>
      <property-change-detector
        :left="selectedObjectModelProperty"
        :right="selectedObjectModelProperty.publishedEquivalent"
        :properties="[['datetime_mode']]"
      />
    </template>
    <template #timeFormatLabel>
      <property-change-detector
        :left="selectedObjectModelProperty"
        :right="selectedObjectModelProperty.publishedEquivalent"
        :properties="[['time_format']]"
      />
    </template>
    <template #minLabel>
      <property-change-detector
        :left="selectedObjectModelProperty"
        :right="selectedObjectModelProperty.publishedEquivalent"
        :properties="[['min_datetime_type', 'type'], [formattedMinDatetimeFunction, 'value']]"
      />
    </template>
    <template #maxLabel>
      <property-change-detector
        :left="selectedObjectModelProperty"
        :right="selectedObjectModelProperty.publishedEquivalent"
        :properties="[['max_datetime_type', 'type'], [formattedMaxDatetimeFunction, 'value']]"
      />
    </template>
  </date-min-maxable>

  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.autoIncrementable)" id="pn-autoincrement">
    <panel-title>
      First Number
      <help-tooltip text="This will be the first number in the sequence of in the auto-incremented field. If you set a number is already used by an object of the same model, the system will start at an unused number."></help-tooltip>
    </panel-title>

    <panel-item>
      <number-input
        v-model="selectedObjectModelProperty.increment_start"
        placeholder=""
        id="increment_start"
        :min="0"
        name="increment_start"
      ></number-input>
    </panel-item>
  </panel-block>

  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.autoIncrementable)">
    <panel-title>
      Resetting Numbering on Date
      <help-tooltip text="This will allow the sequence to reset back to the first number at the start of a new calendar year (Jan 1st), or Maryland fiscal year (July 1st), or within every month throughout. The date will be appended to the number as a prefix."></help-tooltip>
    </panel-title>

    <panel-item>
      <a-select
        id="increment_reset_type"
        :options="incResetOptions"
        v-model="selectedObjectModelProperty.increment_reset_type"
        compress
      >
      </a-select>

      <p v-if="autoIncrementExample">
        Example: {{ autoIncrementExample }}
      </p>
    </panel-item>
  </panel-block>

  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.derivedValuable)">
    <panel-title>Create Formula</panel-title>
    <panel-item>
      <formula-wrapper
        field-name="derived_value"
        :dynamic-options="dynamicOptions"
        serverside-warning-tooltip
      >
        <template #value="slotProps">
          <expandable-textarea
            v-model="slotProps.model[slotProps.fieldName]"
            id="formulaValue"
            type="text"
            placeholder="Enter a formula"
            is-multiline
          ></expandable-textarea>
        </template>
      </formula-wrapper>
    </panel-item>
  </panel-block>

  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.fileAttachable)">
      <panel-item>
        <label :for="selectedObjectModelProperty.idForProp('file_attachment_type')">
          Upload Mode
          <property-change-detector
            :left="selectedObjectModelProperty"
            :right="selectedObjectModelProperty.publishedEquivalent"
            :properties="[['file_attachment_type', 'type']]"
          /> </label>
        <a-select
          v-model="selectedObjectModelProperty.file_attachment_type"
          :options="fileAttachmentOptions"
          :id="selectedObjectModelProperty.idForProp('file_attachment_type')"
          compress
        ></a-select>
      </panel-item>
  </panel-block>

  <security-maskable-config v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.securityMaskable)"
                            determinant-field-name="security_maskable_enabled"
                            :model="selectedObjectModelProperty"
                            :v-option-sets="securityMaskableViewersVOptionSets"
  ></security-maskable-config>

  <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.loggable)">
    <panel-item>
      <row>
        <column>
          <label :for="selectedObjectModel.idForProp('is_logged')">Capture in Change Log?</label>
        </column>
        <suffix>
          <help-tooltip text="To track changes to this property select 'Yes'"></help-tooltip>
        </suffix>
      </row>
      <toggle-select
        :id="selectedObjectModel.idForProp('is_logged')"
        compress
        v-model="selectedObjectModelProperty.is_logged"
      ></toggle-select>
    </panel-item>
  </panel-block>
</div>
